import {
  EntityMutationResultStatus,
  type EntityMutationResultEntityFragment,
  type EntityMutationResultFragment,
  type EntityMutationViolationFragment,
} from '#graphql-operations'
import type { GraphqlMiddlewareMutation } from '#nuxt-graphql-middleware/generated-types'

type EntityMutationMutations = Pick<
  GraphqlMiddlewareMutation,
  | 'createOrderRequest'
  | 'createNewsletterSubscription'
  | 'createConsultationRequest'
>

type UseEntityMutation<M, R> = {
  /**
   * Execute the mutation using the given input values.
   */
  submit: (input: M) => Promise<boolean>

  /**
   * The raw mutation result.
   */
  result: ComputedRef<R | null>

  /**
   * Whether the GraphQL request is currently being executed.
   */
  isLoading: ComputedRef<boolean>

  /**
   * The mutation status.
   */
  status: ComputedRef<EntityMutationResultStatus | null>

  /**
   * The resulting entity after a successful mutation.
   */
  entity: ComputedRef<EntityMutationResultEntityFragment | null>

  /**
   * Errors from either GraphQL or during execution of the mutation.
   */
  errors: ComputedRef<string[]>

  /**
   * Mutation violations.
   */
  violations: ComputedRef<EntityMutationViolationFragment[]>

  /**
   * The errors for the inputs.
   */
  inputErrors: ComputedRef<Record<string, string[]>>

  /**
   * True if the mutation was executed successfully.
   */
  isFinished: ComputedRef<boolean>
}

type UseEntityMutationOptions = {
  /**
   * Map propertyPath from violations to FormKit node ID.
   */
  propertyPathMap?: Record<string, string>
}

/**
 * Perform entity mutations via GraphQL mutations.
 */
export default function <
  T extends keyof EntityMutationMutations,
  M extends EntityMutationMutations[T][0]['input'],
  R extends EntityMutationMutations[T][2]['mutationResult'],
>(name: T, options?: UseEntityMutationOptions): UseEntityMutation<M, R> {
  const result = ref<EntityMutationResultFragment | null>(null)
  const isMakingRequest = ref(false)

  async function submit(input: M) {
    // Return if the submissions was already executed successfully.
    if (isFinished.value) {
      return true
    }
    isMakingRequest.value = true
    await useGraphqlMutation({
      name,
      variables: {
        input,
      } as any,
    }).then((v) => {
      const newResult = v?.data?.mutationResult
      const newErrors = newResult?.errors || []

      // No result, something went terribly wrong.
      if (!newResult) {
        newErrors.push(
          'Es ist ein unerwarteter Fehler aufgetreten. Bitte versuchen Sie es später noch einmal.',
        )
      }
      result.value = {
        ...newResult,
        status: newResult?.status,
        errors: newErrors,
        entity: newResult?.entity,
        violations: newResult?.violations,
      }
    })

    isMakingRequest.value = false
    return isFinished.value
  }

  const isLoading = computed<boolean>(() => isMakingRequest.value)

  const status = computed<EntityMutationResultStatus | null>(
    () => result.value?.status || null,
  )

  const entity = computed<EntityMutationResultEntityFragment | null>(
    () => result.value?.entity || null,
  )
  const errors = computed<string[]>(() => result.value?.errors || [])
  const violations = computed<EntityMutationViolationFragment[]>(
    () => result.value?.violations || [],
  )

  const isFinished = computed(() => {
    if (errors.value.length) {
      return false
    }

    if (violations.value.length) {
      return false
    }

    return (
      status.value === EntityMutationResultStatus.SAVED_NEW ||
      status.value === EntityMutationResultStatus.SAVED_UPDATED
    )
  })

  const rawResult = computed(() => (result.value as R) || null)

  const propertyPathMap = options?.propertyPathMap || {}

  const inputErrors = computed(() => {
    return violations.value.reduce<Record<string, string[]>>((acc, v) => {
      const field = v.propertyPath
      if (field) {
        const property = propertyPathMap[field] || field
        if (!acc[property]) {
          acc[property] = []
        }

        acc[property].push(v.message)
      }
      return acc
    }, {})
  })

  return {
    submit,
    isLoading,
    status,
    entity,
    errors,
    violations,
    isFinished,
    result: rawResult,
    inputErrors,
  }
}
